import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SearchComponent from '../../../components/ui/SearchComponent';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import CreateTeamModal from './CreateTeamModal';
import EditTeamModal from './EditTeamModal';
import TeamDetailsAccordion from './TeamDetailsAccordion';
import { deleteTeam, getTeamsByCityOfficial } from '../../../services/teamService';

// Additional imports for new filtering features


// Fixed import path - changed from supabaseClient to supabase.js
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const TeamManagementTab = ({ filterByCityOfficial }) => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [teams, setTeams] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [expandedTeamId, setExpandedTeamId] = useState(null);

  // State for city official filter
  const [cityOfficialFilter, setCityOfficialFilter] = useState(filterByCityOfficial || null);

  // Check if current user is city official
  const isCityOfficial = userProfile?.role === 'city_official';

  // Update filter if prop changes
  useEffect(() => {
    if (filterByCityOfficial) {
      setCityOfficialFilter(filterByCityOfficial);
    }
  }, [filterByCityOfficial]);

  // Load teams from Supabase with optional city official filter
  const loadTeams = async () => {
    try {
      setLoading(true);
      setError('');
      
      let processedTeams = [];

      // If filtering by city official, use the dedicated service function
      if (cityOfficialFilter) {
        const data = await getTeamsByCityOfficial(cityOfficialFilter);
        
        // Transform data to match expected format
        processedTeams = data?.map(team => ({
          id: team?.id,
          teamNumber: team?.team_number,
          teamName: team?.team_name,
          description: team?.description,
          problemStatement: team?.problem_statement,
          mentor: team?.manager,
          mentorEmail: team?.manager_email,
          cityOfficial: team?.city_official,
          city: team?.city,
          category: team?.category,
          finalDueDate: team?.final_due_date,
          status: team?.status,
          workSummary: team?.work_summary,
          currentMembers: team?.members?.filter(m => m?.role === 'student')?.length || 0,
          maxMembers: team?.max_members,
          createdAt: team?.created_at,
          stageRequirements: team?.stage_requirements,
          members: team?.members?.map(profile => ({
            id: profile?.id,
            userId: profile?.id,
            fullName: profile?.full_name,
            email: profile?.email,
            role: profile?.role,
            organization: profile?.organization,
            skills: profile?.skills,
            isLeader: false
          })) || [],
          deliverables: []
        })) || [];
      } else {
        // Original query for PCF Admin (no filter)
        let query = supabase?.from('teams')?.select(`
          *,
          user_profiles!user_profiles_team_id_fkey (
            id,
            full_name,
            email,
            role,
            organization,
            skills
          )
        `);

        const { data, error: fetchError } = await query?.order('team_number', { ascending: true });

        if (fetchError) throw fetchError;

        // Transform data to match expected format with all team fields from database
        processedTeams = data?.map(team => ({
          id: team?.id,
          teamNumber: team?.team_number,
          teamName: team?.team_name,
          description: team?.description,
          problemStatement: team?.problem_statement,
          mentor: team?.manager,
          mentorEmail: team?.manager_email,
          cityOfficial: team?.city_official,
          city: team?.city,
          category: team?.category,
          finalDueDate: team?.final_due_date,
          status: team?.status,
          workSummary: team?.work_summary,
          currentMembers: team?.user_profiles?.filter(profile => profile?.role === 'student')?.length || 0,
          maxMembers: team?.max_members,
          createdAt: team?.created_at,
          stageRequirements: team?.stage_requirements,
          members: team?.user_profiles?.map(profile => ({
            id: profile?.id,
            userId: profile?.id,
            fullName: profile?.full_name,
            email: profile?.email,
            role: profile?.role,
            organization: profile?.organization,
            skills: profile?.skills,
            isLeader: false
          })) || [],
          deliverables: []
        })) || [];
      }

      setTeams(processedTeams);
    } catch (err) {
      console.error('Error loading teams:', err);
      setError(err?.message || 'Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  // Handle team creation
  const handleTeamCreated = async (newTeam) => {
    setShowCreateModal(false);
    await loadTeams(); // Refresh the team list
  };

  // Handle team deletion
  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm('Are you sure you want to delete this team? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteTeam(teamId);
      await loadTeams(); // Refresh the team list
    } catch (err) {
      console.error('Error deleting team:', err);
      setError(err?.message || 'Failed to delete team');
    }
  };

  // Handle team edit
  const handleEditTeam = (team) => {
    setSelectedTeam(team);
    setShowEditModal(true);
  };

  // Handle team updated
  const handleTeamUpdated = async () => {
    setShowEditModal(false);
    setSelectedTeam(null);
    await loadTeams(); // Refresh the team list
  };

  // Handle search
  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  // Toggle accordion expansion
  const toggleTeamExpansion = (teamId) => {
    setExpandedTeamId(expandedTeamId === teamId ? null : teamId);
  };

  // Load teams on component mount
  useEffect(() => {
    loadTeams();
  }, []);

  // Filter teams based on search
  const filteredTeams = teams?.filter(team => 
    team?.teamName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    team?.teamNumber?.toString()?.includes(searchQuery) ||
    team?.category?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    team?.city?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  ) || [];

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h3 className="text-base sm:text-lg font-semibold text-foreground mb-1 sm:mb-2">Team Management</h3>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Manage all teams, track their progress, and oversee project stages
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <SearchComponent
            placeholder="Search teams..."
            onSearch={handleSearch}
            onClear={handleClearSearch}
            searchScope="all"
            expandable={true}
            className="w-full sm:w-auto"
          />
          {!isCityOfficial && (
            <Button
              variant="default"
              iconName="Plus"
              iconPosition="left"
              iconSize={16}
              onClick={() => setShowCreateModal(true)}
              className="w-full sm:w-auto"
            >
              Create Team
            </Button>
          )}
        </div>
      </div>
  
      {/* Error Display */}
      {error && (
        <div className="bg-error/10 border border-error/20 rounded-lg p-3 sm:p-4">
          <div className="flex items-center space-x-2">
            <Icon name="AlertCircle" size={16} className="text-error" />
            <p className="text-sm sm:text-base text-error font-medium">Error</p>
          </div>
          <p className="text-xs sm:text-sm text-error/80 mt-1">{error}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={loadTeams}
            className="mt-2"
            iconName="RefreshCw"
            iconPosition="left"
          >
            Retry
          </Button>
        </div>
      )}
      {/* Teams List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3]?.map(i => (
            <div key={i} className="animate-pulse bg-card border border-border rounded-lg p-4 sm:p-6">
              <div className="h-5 sm:h-6 bg-muted rounded w-1/2 sm:w-1/3 mb-3"></div>
              <div className="h-3 sm:h-4 bg-muted rounded w-3/4 sm:w-2/3 mb-2"></div>
              <div className="h-3 sm:h-4 bg-muted rounded w-2/3 sm:w-1/2"></div>
            </div>
          ))}
        </div>
      ) : filteredTeams?.length > 0 ? (
        <div className="space-y-3">
          {filteredTeams?.map(team => (
            <TeamDetailsAccordion
              key={team?.id}
              team={team}
              isExpanded={expandedTeamId === team?.id}
              onToggle={() => toggleTeamExpansion(team?.id)}
              onDelete={() => handleDeleteTeam(team?.id)}
              onEdit={() => handleEditTeam(team)}
              onUpdate={loadTeams}
            />
          ))}
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg p-8 sm:p-12 text-center">
          <Icon name="Users" size={48} className="sm:w-16 sm:h-16 text-muted-foreground mx-auto mb-3 sm:mb-4" />
          <h3 className="text-base sm:text-lg font-medium text-foreground mb-2">No Teams Found</h3>
          <p className="text-xs sm:text-sm text-muted-foreground mb-4">
            {searchQuery 
              ? 'No teams match your search criteria. Try adjusting your filters.' :'Get started by creating your first team.'}
          </p>
          {!searchQuery && !isCityOfficial && (
            <Button
              variant="default"
              onClick={() => setShowCreateModal(true)}
              iconName="Plus"
              iconPosition="left"
              size="sm"
            >
              Create First Team
            </Button>
          )}
        </div>
      )}
      {/* Create Team Modal */}
      <CreateTeamModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onTeamCreated={handleTeamCreated}
      />

      {/* Edit Team Modal */}
      <EditTeamModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedTeam(null);
        }}
        team={selectedTeam}
        onTeamUpdated={handleTeamUpdated}
      />
    </div>
  );
};

export default TeamManagementTab;